<template>
  <div class="modal__wrap--bg cmp-modal is-active" :id="modalId" style="display: flex;">
    <div class="modal-wrap-container modal-wd1020">
      <!-- 모달 헤더 -->
      <div class="modal-header">
        <h2 class="title" id="rebate-modal-title" tabindex="-1">할인/감면 신청</h2>
      </div>

      <!-- 모달 바디 -->
      <div class="modal-body-container ht-full">
        <!-- 진행 단계 표시 -->
        <div class="gray-box yakguan-step">
          <ol>
            <li :class="{'is-active': currentStep === 1}" :aria-current="currentStep === 1 ? 'step' : null">
              <span>1</span> 약관<br class="mo-only">동의
            </li>
            <li :class="{'is-active': currentStep === 2}" :aria-current="currentStep === 2 ? 'step' : null">
              <span>2</span> 공공마이데이터 <br class="mo-only">불러오기
            </li>
          </ol>
        </div>

        <!-- 단계별 컴포넌트 동적 렌더링 -->
        <component
            :is="currentStepComponent"
            :course-no="courseNo"
            :step-data="stepData[currentStep]"
            :auth-params="authParams"
            @step-validated="handleStepValidation"
            @update-data="handleDataUpdate"
            @next-step="goToNextStep"
            @prev-step="goToPrevStep"
            @application-success="handleApplicationSuccess"
            @application-error="handleApplicationError"
        ></component>
      </div>

      <!-- 모달 푸터 -->
      <div class="modal-footer">

        <!-- 1단계: 동의하지 않음 버튼 -->
        <button
            v-if="currentStep === 1"
            type="button"
            class="btn btn-secondary btn-md button-wid150"
            @click="closeModal"
        >
          동의하지 않음
        </button>

        <!-- 주 액션 버튼 -->
        <button
            type="button"
            class="btn btn-primary btn-md button-wid150"
            @click="handleFooterButtonClick"
            :disabled="isSubmitting"
        >
          {{ footerButtonText }}
        </button>
      </div>

      <!-- 모달 닫기 버튼 -->
      <button type="button" class="btn btn-close only-icon close-modal" aria-label="닫기" @click="closeModal" title="닫기">
        <i class="ico ico-x-bl"></i>
      </button>
    </div>
  </div>
</template>

<script>
// 동적 컴포넌트 import (지연 로딩)
const RebateStep1Agreement = () => import('./components/RebateStep1Agreement.vue');
const RebateStep2AppSubmit = () => import('./components/RebateStep2AppSubmit.vue');

export default {
  name: 'RebateApplicationModal',

  components: {
    RebateStep1Agreement,
    RebateStep2AppSubmit,
  },

  props: {
    courseNo: {
      type: [String, Number],
      required: true,
    },
    modalId: {
      type: String,
      default: 'rebateApplicationModal',
    },
    authParams: {
      type: Object,
      default: () => ({
        reqInfo: '',
        retUrl: '',
        ipinReqInfo: '',
        ipinRetUrl: ''
      }),
    },
  },

  data() {
    return {
      currentStep: 1,
      canProceed: false,
      isSubmitting: false,

      // 단계별 데이터 저장
      stepData: {
        1: {
          agreements: [
            {
              id: 'agreement1',
              title: '개인정보의 수집․이용(개인식별정보)에 동의합니다.',
              checked: false,
            },
            {
              id: 'agreement2', 
              title: '수집된 개인정보를 제3자 제공․활용에 동의합니다.',
              checked: false,
            },
          ],
          isValid: false,
        },
        2: {
          discountType: '',
          name: '',
          socialNumber1: '',
          socialNumber2: '',
          identityVerified: false,
          isValid: false,
          mddsAgreementChecked: false, // MDDS 동의 상태 추가
          mddsJbRst: '', // MDDS_JB_RST 데이터 보관용
        },
      },

      // 컴포넌트 매핑
      stepComponents: {
        1: 'RebateStep1Agreement',
        2: 'RebateStep2AppSubmit',
      },
    };
  },

  computed: {
    currentStepComponent() {
      return this.stepComponents[this.currentStep];
    },

    footerButtonText() {
      if (this.currentStep === 1) {
        return '동의';
      } else if (this.currentStep === 2) {
        return this.isSubmitting ? '신청 중...' : '신청하기';
      }
      return '다음단계';
    },
  },

  methods: {
    /**
     * 단계별 검증 상태 처리
     */
    handleStepValidation(isValid) {
      this.canProceed = isValid;
      this.stepData[this.currentStep].isValid = isValid;
    },

    /**
     * 단계별 데이터 업데이트 처리
     */
    handleDataUpdate(data) {
      this.stepData[this.currentStep] = {
        ...this.stepData[this.currentStep],
        ...data,
      };
    },

    /**
     * 다음 단계로 이동
     */
    goToNextStep() {
      // 1단계에서만 약관 동의 검증
      if (this.currentStep === 1 && !this.stepData[this.currentStep].isValid) {
        alert('모든 약관에 동의해주세요.');
        return;
      }

      if (this.currentStep < 2) {
        // 1단계에서 2단계로 이동할 때 MDDS 동의 상태 전달
        if (this.currentStep === 1) {
          const step1Data = this.stepData[1];
          const mddsAgreementChecked = step1Data.mddsAgreementChecked || false;
          
          // 2단계 데이터에 MDDS 동의 상태 설정
          this.stepData[2].mddsAgreementChecked = mddsAgreementChecked;
        }
        
        this.currentStep += 1;
        this.canProceed = true; // 2단계에서는 항상 활성화
        this.scrollToTop();
      }
    },

    /**
     * 이전 단계로 이동
     */
    goToPrevStep() {
      if (this.currentStep > 1) {
        this.currentStep -= 1;
        this.canProceed = this.stepData[this.currentStep].isValid || false;
        this.scrollToTop();
      }
    },

    /**
     * 푸터 버튼 클릭 처리
     */
    handleFooterButtonClick() {
      if (this.currentStep === 2) {
        // 2단계에서는 신청 처리
        this.submitRebateApplication();
      } else if (this.currentStep === 1) {
        // 1단계에서는 약관 동의 검증 후 다음 단계로
        if (!this.stepData[this.currentStep].isValid) {
          alert('개인정보 수집 및 이용 동의 항목을 동의해주세요.');
          return;
        }
        this.goToNextStep();
      }
    },

    /**
     * 할인/감면 신청 처리
     */
    submitRebateApplication() {
      if (this.isSubmitting) {
        return;
      }

      // 할인구분만 필수 체크
      const step2Data = this.stepData[2];
      if (!step2Data.discountType) {
        alert('할인구분을 입력하세요.');
        return;
      }

      // 신청 확인
      if (!confirm('할인/면제 신청을 진행하시겠습니까?')) {
        return;
      }

      this.isSubmitting = true;
      const payload = this.buildApplicationPayload();

      // API 호출
      fetch(`/api/courses/discount/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
      })
      .then((response) => {
        if (response.ok) {
          return response.json().then((result) => {
            this.handleApplicationSuccess(result);
            return result;
          });
        }
        return response.json().then((errorData) => {
          const errorMessage = errorData.msg || errorData.message || '할인/감면 신청 중 오류가 발생했습니다.';
          this.handleApplicationError(errorMessage);
          throw new Error(errorMessage);
        }).catch(() => {
          const defaultMessage = `할인/감면 신청 실패 (HTTP ${response.status})`;
          this.handleApplicationError(defaultMessage);
          throw new Error(defaultMessage);
        });
      })
      .catch((error) => {
        this.handleApplicationError(error.message || error);
        throw error;
      })
      .finally(() => {
        this.isSubmitting = false;
      });
    },

    /**
     * 신청 데이터 페이로드 생성
     */
    buildApplicationPayload() {
      const step1Data = this.stepData[1];
      const step2Data = this.stepData[2];

      return {
        courseNo: this.courseNo,
        discountType: step2Data.discountType,
        name: step2Data.name,
        socialNumber: step2Data.socialNumber1 + step2Data.socialNumber2,
        agreements: step1Data.agreements.map(agreement => ({
          id: agreement.id,
          agreed: agreement.checked,
        })),
        identityVerified: step2Data.identityVerified,
        mddsJbRst: step2Data.mddsJbRst || null, // MDDS_JB_RST 데이터 포함
        // MDDS 개별 결과 데이터 추가
        L001_RST: step2Data.mddsResult ? step2Data.mddsResult.L001_RST : null,
        L015_RST: step2Data.mddsResult ? step2Data.mddsResult.L015_RST : null,
        L398_RST: step2Data.mddsResult ? step2Data.mddsResult.L398_RST : null,
      };
    },

    /**
     * 신청 성공 처리
     */
    handleApplicationSuccess(result) {
      alert('저장되었습니다.');
      this.closeModal();
      
      // 페이지 새로고침으로 상태 업데이트
      if (typeof window !== 'undefined') {
        window.location.reload();
      }
    },

    /**
     * 신청 실패 처리
     */
    handleApplicationError(error) {
      const errorMessage = typeof error === 'string' ? error : (error.message || '할인/감면 신청 중 오류가 발생했습니다.');
      alert(errorMessage);
    },

    /**
     * 모달 닫기
     */
    closeModal() {
      // 기존 JB 패턴의 모달 닫기 함수 호출
      if (typeof window.closeRebateApplicationModal === 'function') {
        window.closeRebateApplicationModal();
      } else if (typeof window !== 'undefined' && window.closeModal) {
        window.closeModal(event);
      } else {
        // 폴백: 모달 숨기기
        this.$el.style.display = 'none';
      }
    },

    /**
     * 모달 스크롤을 상단으로 이동하고 헤더로 포커스 이동
     */
    scrollToTop() {
      this.$nextTick(() => {
        const modalBody = this.$el.querySelector('.modal-body-container');
        if (modalBody) {
          modalBody.scrollTop = 0;
        }
        const titleEl = this.$el.querySelector('h2.title[tabindex="-1"]');
        if (titleEl) {
          titleEl.focus();
        }
      });
    },

    /**
     * 컴포넌트 초기화
     */
    initializeModal() {
      this.currentStep = 1;
      this.canProceed = false;
      this.isSubmitting = false;
      
      // 단계별 데이터 초기화
      this.stepData[1].agreements.forEach(agreement => {
        agreement.checked = false;
      });
      this.stepData[1].isValid = false;
      
      Object.assign(this.stepData[2], {
        discountType: '',
        name: '',
        socialNumber1: '',
        socialNumber2: '',
        identityVerified: false,
        isValid: false,
        mddsAgreementChecked: false, // MDDS 동의 상태 초기화
        mddsJbRst: '', // MDDS_JB_RST 데이터 초기화
      });
    },
  },

  mounted() {
    this.initializeModal();
  },
};
</script>



// WEBPACK FOOTER //
// RebateApplicationModal.vue?7c92da7b